import type { ErrorEvent, EventHint } from '@sentry/react';
import axios, { type AxiosError } from 'axios';
import { ClientError } from 'graphql-request';

type TransformRule = {
  id: string;
  transform: (event: ErrorEvent, hint?: EventHint) => void;
};
type StaticSubRoutes = ReadonlyArray<string>;

const ROUTES_WITH_STRING_IDS: Record<string, StaticSubRoutes> = {
  data: ['locations', 'battery_utilization', 'multi'],
  edges: [],
  events: ['units', 'robocalls', 'emails'],
  'greener-units': ['recent', 'available'],
  microgrids: [],
  nodes: [],
  'process-jobs': [],
  shared: [],
  units: ['alerts', 'alertack'],
};

const buildUrlTemplate = (url: string): string => {
  const urlSegments = url.split('?')[0].split('/').filter(Boolean);
  return urlSegments
    .map((segment, index) => {
      if (Number.isFinite(Number(segment))) {
        return ':id';
      }
      const parentSegment = urlSegments[index - 1];
      if (parentSegment in ROUTES_WITH_STRING_IDS) {
        return ROUTES_WITH_STRING_IDS[parentSegment].includes(segment) ? segment : ':id';
      }
      return segment;
    })
    .join('/');
};

const getResponseStatus = (hint?: EventHint): number | undefined => {
  const error = hint?.originalException as { response?: { status?: number } } | undefined;
  return error?.response?.status;
};

const getAxiosError = (hint?: EventHint): AxiosError | undefined => {
  const error = hint?.originalException;
  return axios.isAxiosError(error) ? error : undefined;
};

const getGQLError = (hint?: EventHint): ClientError | undefined => {
  const error = hint?.originalException;
  return error instanceof ClientError ? error : undefined;
};

const getGQLErrorStatus = (gqlError: ClientError): number | undefined => {
  const error = gqlError.response?.errors?.[0];
  return error?.status ?? error?.extensions?.http_status_code ?? error?.extensions?.status ?? gqlError.response?.status;
};

const getAxiosContext = (axiosError: AxiosError): { endpoint: string; method: string } => {
  const url = axiosError.config?.url ?? axiosError.request?.responseURL;
  const endpoint = url ? buildUrlTemplate(url) : 'unknown';
  const method = (axiosError.config?.method ?? 'unknown').toUpperCase();
  return { endpoint, method };
};

const setIssueTitle = ({ event, title }: { event: ErrorEvent; title: string }): void => {
  const issueTitle = event.exception?.values?.[0];
  if (issueTitle) {
    issueTitle.value = title;
  }
};

const getEventStrings = (event: ErrorEvent, hint?: EventHint): string[] => {
  const hintMessage = hint?.originalException instanceof Error ? hint.originalException.message : undefined;
  const breadcrumbTexts =
    event.breadcrumbs?.map(
      (breadcrumb) =>
        `${breadcrumb.category ?? ''} ${breadcrumb.message ?? ''} ${breadcrumb.data ? JSON.stringify(breadcrumb.data) : ''}`
    ) ?? [];

  return [
    event.message,
    ...(event.exception?.values?.map((exceptionValue) => exceptionValue.value).filter(Boolean) ?? []),
    ...breadcrumbTexts,
    hintMessage,
  ]
    .filter((text): text is string => typeof text === 'string' && text.length > 0)
    .map((text) => text.toLowerCase());
};

const muteConditions = [
  // 401 is expected error when the session is expired
  (hint?: EventHint) => getResponseStatus(hint) === 401,
  // Network errors with no response
  (hint?: EventHint) => {
    const axiosError = getAxiosError(hint);
    return axiosError !== undefined && axiosError.response === undefined;
  },
  // ResizeObserver loop — a known browser-level limitation (not a code bug)
  (hint?: EventHint) => {
    const msg = hint?.originalException instanceof Error ? hint.originalException.message : '';
    return msg.includes('ResizeObserver loop');
  },
  // ChunkLoadError — expected error happening when a user has the app open during a deploy
  (hint?: EventHint) => {
    const error = hint?.originalException;
    return error instanceof Error && error.name === 'ChunkLoadError';
  },
  // AbortError/CanceledError — expected errors when requests got cancelled after user navigates away
  (hint?: EventHint) => {
    const error = hint?.originalException;
    return (
      error instanceof Error &&
      (error.name === 'AbortError' || error.name === 'CanceledError' || error.message === 'canceled')
    );
  },
  // Script error — cross-origin errors from browser extensions (no stack trace, not actionable)
  (hint?: EventHint) => {
    const error = hint?.originalException;
    return error instanceof Error && error.message === 'Script error.';
  },
];

const transformRules: TransformRule[] = [
  {
    // Axios 422 validation errors — downgrade to info so volume is visible but noise is low
    id: 'axios-validation-errors',
    transform: (event, hint) => {
      const axiosError = getAxiosError(hint);
      if (!axiosError || axiosError?.response?.status !== 422) {
        return;
      }

      event.level = 'info';

      const { endpoint, method } = getAxiosContext(axiosError);

      event.fingerprint = ['validation', method, endpoint];
      event.tags = { ...event.tags, validation_error: 'true', endpoint, http_method: method };
      setIssueTitle({ event, title: `Validation error: 422 ${method} ${endpoint}` });
    },
  },
  {
    // Axios 403 authorization errors — downgrade to info
    id: 'axios-authorization-error',
    transform: (event, hint) => {
      const axiosError = getAxiosError(hint);
      if (!axiosError || axiosError.response?.status !== 403) {
        return;
      }

      event.level = 'info';

      const { endpoint, method } = getAxiosContext(axiosError);

      event.fingerprint = ['authorization-error', method, endpoint];
      event.tags = { ...event.tags, endpoint, http_method: method };
      setIssueTitle({ event, title: `Authorization Error: 403 ${method} /${endpoint}` });
    },
  },
  {
    // Unleash proxy auth/fetch failures
    id: 'unleash-login-required',
    transform: (event, hint) => {
      const hasAuth0Stack = event.exception?.values?.some((ex) =>
        ex.stacktrace?.frames?.some(
          (frame) => frame.filename?.includes('@auth0') || frame.filename?.includes('auth0-spa-js')
        )
      );

      const texts = getEventStrings(event, hint);
      const hasLoginRequired = texts.some((entry) => entry.includes('login required'));
      const hasFetchError = texts.some(
        (entry) =>
          entry.includes('unleash: unable to fetch') ||
          (entry.includes('failed to fetch') &&
            (entry.includes('unleash') || entry.includes('proxy-client') || entry.includes('feature toggles')))
      );
      const hasUnleashRef = texts.some(
        (entry) => entry.includes('unleash') || entry.includes('proxy-client') || entry.includes('feature toggles')
      );

      if ((hasLoginRequired || hasFetchError) && hasUnleashRef && !hasAuth0Stack) {
        event.level = 'warning';
        event.fingerprint = ['unleash-proxy-failure'];
        setIssueTitle({ event, title: 'Unleash: feature flags proxy unreachable' });
        event.tags = { ...event.tags, error_type: 'unleash_proxy_failure' };
      }
    },
  },
  {
    // Amplitude config failures
    id: 'amplitude-config-load-failed',
    transform: (event, hint) => {
      const texts = getEventStrings(event, hint);
      const hasLoadError = texts.some((text) => text.includes('load failed') || text.includes('failed to fetch'));
      const hasAmplitudeRef = texts.some(
        (text) => text.includes('sr-client-cfg.amplitude.com') || text.includes('amplitude.com/config')
      );

      if (hasLoadError && hasAmplitudeRef) {
        event.level = 'warning';
        event.fingerprint = ['amplitude-config-failure'];
        setIssueTitle({ event, title: 'Amplitude: failed to load SDK config' });
        event.tags = { ...event.tags, error_type: 'amplitude_config_failure' };
      }
    },
  },
  {
    // group axios errors so each unique api failure gets its own issue
    id: 'axios-error-grouping',
    transform: (event, hint) => {
      const axiosError = getAxiosError(hint);
      if (!axiosError) {
        return;
      }

      const { endpoint, method } = getAxiosContext(axiosError);
      const httpStatus = axiosError.response?.status ?? 'network-error';

      if (httpStatus === 403 || httpStatus === 422) {
        return;
      }

      event.fingerprint = ['axios-error', method, endpoint, String(httpStatus)];
      event.tags = { ...event.tags, endpoint, http_method: method };
      setIssueTitle({ event, title: `HTTP ${httpStatus} ${method} /${endpoint}` });
    },
  },
  {
    // group GQL errors so each unique operation failure gets its own issue
    id: 'gql-error-grouping',
    transform: (event, hint) => {
      const gqlError = getGQLError(hint);
      if (!gqlError) {
        return;
      }
      const gqlStatus = getGQLErrorStatus(gqlError) ?? gqlError.response?.status ?? 'unknown';

      const error = gqlError.response?.errors?.[0] as { message?: string; path?: string[] } | undefined;
      const operation = error?.path?.[0] ?? 'unknown';

      event.fingerprint = ['gql-error', operation, String(gqlStatus)];
      event.tags = { ...event.tags, gql_operation: operation, gql_status: String(gqlStatus) };
      event.extra = { ...event.extra, gql_error_message: error?.message };

      setIssueTitle({ event, title: `GQL ${gqlStatus} ${operation}` });
    },
  },
  {
    // 502/503/504 errors (the server is temporarily down - BE issues)
    id: 'server-unavailable',
    transform: (event, hint) => {
      const status = getResponseStatus(hint);
      if (status === 502 || status === 503 || status === 504) {
        event.level = 'warning';
        event.fingerprint = ['server-unavailable', String(status)];
        event.tags = { ...event.tags, error_type: 'server_unavailable' };

        setIssueTitle({ event, title: `Server Unavailable (HTTP ${status})` });
      }
    },
  },
];

export const getBeforeSendConfig = (event: ErrorEvent, hint?: EventHint): ErrorEvent | null => {
  if (muteConditions.some((isMuted) => isMuted(hint))) {
    return null;
  }
  transformRules.forEach((rule) => rule.transform(event, hint));
  return event;
};
